// Copyright (c) 2026 Watch Tower Bible and Tract Society of Pennsylvania
// All Rights Reserved. Proprietary and confidential.
// Unauthorized copying, modification, or distribution is strictly prohibited.
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../ui/button';
import {
  LayoutDashboard,
  FileText,
  X,
  BookOpen,
  Wrench,
  ChevronRight,
  ClipboardList,
  ClipboardCheck,
  FolderLock,
  FileWarning,
  ScrollText,
  ExternalLink,
  Bot,
  Users,
  TrendingUp,
  Briefcase,
} from 'lucide-react';

const NAVIGATION = [
  { name: 'Visão Geral', href: '/dashboard', icon: LayoutDashboard, tourId: 'tour-nav-dashboard' },
  { name: 'Documentos', href: '/documents', icon: FileText, tourId: 'tour-nav-documents' },
  { name: 'S-25b Congregação', href: '/audit-form?type=s25b', icon: ClipboardList, tourId: 'tour-nav-audit' },
  { name: 'S-24 Recibo', href: '/audit-form?type=s24', icon: ClipboardList, area: 'congregacao' },
  { name: 'S-45b Manutenção', href: '/audit-form?type=s45b', icon: Wrench },
  { name: 'S-26 Folha de Contas', href: '/audit-form?type=s26', icon: ClipboardList, area: 'manutencao' },
  { name: 'S-44 Relatório Mensal', href: '/audit-form?type=s44', icon: ClipboardList, area: 'manutencao' },
  { name: 'Previsão de Gastos', href: '/previsao-gastos', icon: TrendingUp, area: 'manutencao' },
  { name: 'Auditoria Trimestral', href: '/auditoria-trimestral', icon: ClipboardCheck, roles: ['auditor', 'secretario', 'servo_contas'] },
  { name: 'Aprovações Permanentes', href: '/aprovacoes-permanentes', icon: FolderLock, roles: ['auditor', 'secretario', 'servo_contas'] },
  { name: 'Discrepâncias', href: '/discrepancias', icon: FileWarning, roles: ['auditor', 'secretario', 'servo_contas'] },
];

function isNavActive(item, location) {
  const itemBase = item.href.split('?')[0];
  if (location.pathname !== itemBase) return false;
  if (item.href.includes('?')) {
    return location.search.includes(item.href.split('?')[1]);
  }
  return !location.search;
}

function navTestId(name) {
  // Normalize all whitespace and diacritics for stable test selectors.
  return `nav-${name.toLowerCase().replace(/\s+/g, '-')}`;
}

/**
 * Application sidebar (logo + area indicator + nav + user card).
 * Hidden off-canvas on mobile; sticky on desktop.
 */
export default function Sidebar({
  open,
  onClose,
  user,
  isAdminUser,
  currentArea,
  onChangeArea,
  onOpenTravelerVisit,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const AreaIcon = currentArea.icon;
  const isCongregacao = currentArea.shortName === 'Congregação';

  const go = (href) => {
    if (href.includes('type=s25b') || href.includes('type=s24')) localStorage.setItem('selectedArea', 'congregacao');
    else if (href.includes('type=s45b') || href.includes('type=s26') || href.includes('type=s44')) localStorage.setItem('selectedArea', 'manutencao');
    navigate(href);
    onClose();
  };

  return (
    <aside
      className={`
        fixed top-0 left-0 z-50 h-full w-72 bg-card/95 backdrop-blur-md border-r border-border
        transform transition-transform duration-300 ease-out
        lg:translate-x-0
        ${open ? 'translate-x-0' : '-translate-x-full'}
      `}
    >
      <div className="flex flex-col h-full">
        {/* Logo */}
        <div className="flex items-center justify-between h-[72px] px-6 border-b border-border">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="JW Donate Audit Logo" className="w-10 h-10 object-contain" />
            <span className="font-extrabold text-lg tracking-tight text-foreground font-heading">
              JW Audit
            </span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden hover:bg-accent rounded-xl"
            onClick={onClose}
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Current Area */}
        <div className="px-4 py-5 border-b border-border">
          <button
            onClick={onChangeArea}
            id="tour-area-selector"
            className={`w-full flex items-center gap-3 p-4 rounded-xl ${currentArea.bgColor} border ${currentArea.borderColor} hover:shadow-md transition-all duration-200 group dark:bg-opacity-20`}
            data-testid="change-area-button"
          >
            <div
              className={`w-10 h-10 rounded-xl ${
                isCongregacao
                  ? 'bg-congregation/10 dark:bg-congregation/20'
                  : 'bg-maintenance/10 dark:bg-maintenance/20'
              } flex items-center justify-center`}
            >
              <AreaIcon className={`w-5 h-5 ${currentArea.color}`} />
            </div>
            <div className="flex-1 min-w-0 text-left">
              <p className={`text-sm font-semibold ${currentArea.color}`}>{currentArea.shortName}</p>
              <p className="text-xs text-muted-foreground truncate">Trocar área</p>
            </div>
            <ChevronRight className={`w-4 h-4 ${currentArea.color} opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all`} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1.5 overflow-y-auto" id="tour-nav-menu">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest px-4 mb-3">
            Menu
          </p>
          {NAVIGATION.filter((item) => {
            if (item.roles && !isAdminUser && !item.roles.includes(user?.role)) return false;
            if (!item.area) return true;
            if (item.area === 'manutencao') return !isCongregacao;
            if (item.area === 'congregacao') return isCongregacao;
            return true;
          }).map((item) => {
            const active = isNavActive(item, location);
            const Icon = item.icon;
            return (
              <button
                key={item.name}
                onClick={() => go(item.href)}
                id={item.tourId}
                data-testid={navTestId(item.name)}
                className={`
                  w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-medium
                  transition-all duration-200
                  ${
                    active
                      ? 'bg-primary text-primary-foreground shadow-lg'
                      : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                  }
                `}
              >
                <Icon className="w-5 h-5" />
                {item.name}
              </button>
            );
          })}

          {/* Visita do Viajante (dialog, não é rota) */}
          {(isAdminUser || ['servo_contas', 'secretario'].includes(user?.role)) && (
            <button
              onClick={() => { onOpenTravelerVisit(); onClose(); }}
              data-testid="nav-visita-do-viajante"
              className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-medium transition-all duration-200 text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <Briefcase className="w-5 h-5" />
              Visita do Viajante
            </button>
          )}

          {/* Help */}
          <div className="pt-4 pb-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest px-4">
              Ajuda
            </p>
          </div>
          <a
            href="/Manual_JW_Audit.html"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-manual-usuario"
            className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-medium transition-all duration-200 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <BookOpen className="w-5 h-5" />
            Manual do Usuário
            <ExternalLink className="w-3.5 h-3.5 ml-auto opacity-50" />
          </a>
          <button
            onClick={() => { navigate('/assistente'); onClose(); }}
            data-testid="nav-assistente"
            className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-medium transition-all duration-200
              ${location.pathname === '/assistente'
                ? 'bg-primary/10 text-primary'
                : 'text-muted-foreground hover:bg-accent hover:text-foreground'}`}
          >
            <Bot className="w-5 h-5" />
            Audite
          </button>

          {/* Admin */}
          {isAdminUser && (
            <>
              <div className="pt-4 pb-2" id="tour-admin-section">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest px-4">
                  Administração
                </p>
              </div>
              <button
                onClick={() => { navigate('/admin/users'); onClose(); }}
                id="tour-admin-users"
                data-testid="nav-admin-users"
                className={`
                  w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-medium
                  transition-all duration-200
                  ${location.pathname === '/admin/users'
                    ? 'bg-primary text-primary-foreground shadow-lg'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground'}
                `}
              >
                <Users className="w-5 h-5" />
                Usuários
              </button>
              <button
                onClick={() => { navigate('/admin/audit-logs'); onClose(); }}
                id="tour-admin-logs"
                data-testid="nav-admin-audit-logs"
                className={`
                  w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-medium
                  transition-all duration-200
                  ${location.pathname === '/admin/audit-logs'
                    ? 'bg-primary text-primary-foreground shadow-lg'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground'}
                `}
              >
                <ScrollText className="w-5 h-5" />
                Logs de Auditoria
              </button>
            </>
          )}
        </nav>

        {/* User card */}
        <div className="p-4 border-t border-border">
          <button
            onClick={() => { navigate('/minha-conta'); onClose(); }}
            data-testid="nav-minha-conta"
            className="w-full flex items-center gap-3 px-4 py-4 rounded-xl bg-muted/50 hover:bg-accent transition-colors text-left group"
          >
            <div className="w-11 h-11 bg-gradient-to-br from-primary/80 to-primary rounded-xl flex items-center justify-center shadow-md">
              <span className="text-primary-foreground font-semibold text-sm">
                {user?.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground truncate">Minha Conta</p>
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
          </button>
        </div>
      </div>
    </aside>
  );
}
